import React from "react";
import { Typography, Box, CardContent, Button } from "@mui/material";
import type { ScientificSource } from "../../types/frontend";
import { sourcesPanelStyles } from "../PanelStyles";

const ABSTRACT_WORD_LIMIT = 1000;

interface ScientificSourceCardContentProps {
  source: ScientificSource;
  isExpanded: boolean;
  onLinkClick: (e: React.MouseEvent, source: ScientificSource) => void;
}

const ScientificSourceCardContent: React.FC<
  ScientificSourceCardContentProps
> = ({ source, isExpanded, onLinkClick }) => {
  const truncateAbstract = (
    text: string | null | undefined,
    wordLimit: number
  ): string => {
    if (!text) return "";
    const words = text.split(/\s+/);
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(" ") + "...";
  };

  return (
    <CardContent sx={sourcesPanelStyles.cardContent}>
      {/* Title section */}
      <Box sx={sourcesPanelStyles.titleSection}>
        <Box sx={sourcesPanelStyles.titleImageAndTextContainer}>
          <Typography
            variant="h6"
            component="div"
            sx={sourcesPanelStyles.sourceTitle}
          >
            {source.title}
            {source.year ? `, ${source.year}` : ""}
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      <Typography
        variant="body2"
        sx={{
          ...sourcesPanelStyles.sourceDescription,
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: isExpanded ? 8 : 3,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {truncateAbstract(source.abstract, ABSTRACT_WORD_LIMIT)}
      </Typography>

      {/* Additional info for scientific sources */}
      {source.venue && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: "0.875rem",
            mt: 1,
          }}
        >
          Published in: {source.venue}
        </Typography>
      )}

      {/* View source button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: "auto",
        }}
      >
        {source.url && (
          <Button
            variant="text"
            onClick={(e) => onLinkClick(e, source)}
            sx={sourcesPanelStyles.viewSourceLink}
          >
            View source
          </Button>
        )}
      </Box>
    </CardContent>
  );
};

export default ScientificSourceCardContent;
